import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Category-filtered photo gallery — mirrors the tabbed gallery pattern used by
 * the AMC reference site (atlanticontheavenue.com/gallery): a row of solid,
 * rectangular black tabs (ALL / COMMUNITY / APARTMENT) sits above a uniform
 * multi-column grid of equally sized tiles. Clicking a tab filters the grid to
 * photos whose `title` matches that category; photos without a title roll into
 * a generic "Featured" bucket. When only one category is present the tab row
 * hides and this collapses to a plain single-view grid.
 *
 * The page hero (HeroSolidBG, `blurbField: property_gallery_blurb`) supplies
 * the "Gallery" title and intro paragraph, so this section is intentionally
 * heading-less — it is just the tabs + grid.
 *
 * Desktop keeps the multi-column grid + lightbox. On mobile the tabs center
 * and wrap, and photos stack as a single inset column with no lightbox
 * (the shared carousel/lightbox from `_galleryShared` isn't a public export
 * of the parent theme package, so the desktop lightbox is inlined here).
 */

/** Desktop breakpoint where the multi-column grid + lightbox apply. */
const GALLERY_DESKTOP_MQ = '(min-width: 640px)';

function isDesktopGalleryViewport() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia(GALLERY_DESKTOP_MQ).matches
  );
}

/** A single item from the property `photo_gallery` collection. */
interface GalleryImage {
  image?: string;
  alt_text?: string;
  title?: string;
  description?: string;
  original_url?: string;
}

/** Photos with no `title` are grouped under this synthetic bucket. */
const UNCATEGORIZED_LABEL = 'Featured';

/** Label of the always-first "show everything" tab. */
const ALL_LABEL = 'All';

/**
 * Group the incoming images by `title`, preserving the first-seen order of
 * categories. Titles are trimmed so trailing whitespace differences don't
 * split a category in two, and blank titles roll up under UNCATEGORIZED_LABEL.
 */
function useCategorizedImages(images: GalleryImage[]) {
  return useMemo(() => {
    const order: string[] = [];
    const groups = new Map<string, GalleryImage[]>();
    for (const img of images) {
      const rawTitle = (img.title || '').trim();
      const bucket = rawTitle.length > 0 ? rawTitle : UNCATEGORIZED_LABEL;
      if (!groups.has(bucket)) {
        groups.set(bucket, []);
        order.push(bucket);
      }
      groups.get(bucket)!.push(img);
    }
    return { order, groups };
  }, [images]);
}

export function GalleryGrid() {
  const images = useCollectionBinding<GalleryImage>('gallerygrid.images', {
    collection: 'photo_gallery',
  });

  const { order: categoryOrder, groups: categoryGroups } =
    useCategorizedImages(images);

  // Only show tabs when there's more than one real category. A single-bucket
  // gallery falls back to the plain masonry — no filter row is helpful.
  const showTabs = categoryOrder.length > 1;

  // Tabs: "All" always leads, followed by each real category. When there's
  // just one category, the tab row is hidden entirely.
  const tabs = useMemo(
    () => (showTabs ? [ALL_LABEL, ...categoryOrder] : [ALL_LABEL]),
    [categoryOrder, showTabs],
  );

  // The tab the visitor has explicitly picked. `null` means "nothing picked
  // yet": the grid still shows every photo, but no tab carries the white
  // highlight — "All" starts black and only turns white on hover or once it is
  // deliberately clicked.
  const [chosenTab, setChosenTab] = useState<string | null>(null);

  // The filter actually in effect. Defaults to All while `chosenTab` is null.
  const activeTab = chosenTab ?? ALL_LABEL;

  // If the underlying collection changes (edit mode override, hidden items,
  // etc.) and the chosen tab no longer exists, drop back to the unpicked state.
  useEffect(() => {
    if (
      chosenTab !== null &&
      chosenTab !== ALL_LABEL &&
      !categoryOrder.includes(chosenTab)
    ) {
      setChosenTab(null);
    }
  }, [chosenTab, categoryOrder]);

  // Images visible under the current tab. "All" preserves the original order
  // from the collection; per-category tabs use the grouped order.
  const filteredImages = useMemo(() => {
    if (activeTab === ALL_LABEL) return images;
    return categoryGroups.get(activeTab) ?? [];
  }, [activeTab, images, categoryGroups]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Clear any open lightbox on tab change — its indices point at the previous
  // filtered slice, so keeping it open would show the wrong image.
  useEffect(() => {
    setSelectedIndex(null);
  }, [activeTab]);

  // Lightbox is desktop-only; close it if the viewport drops to mobile.
  useEffect(() => {
    const mql = window.matchMedia(GALLERY_DESKTOP_MQ);
    const onChange = () => {
      if (!mql.matches) setSelectedIndex(null);
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const openLightbox = useCallback((index: number) => {
    if (!isDesktopGalleryViewport()) return;
    setSelectedIndex(index);
  }, []);

  // Every photo in the active tab is rendered — no pagination / Load More.
  const visibleImages = filteredImages;

  if (images.length === 0) {
    return (
      <section className="bg-white">
        <div className="py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">
            Gallery images will be displayed here.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-grid__container">
        {/* Category tabs — hidden when there's only one bucket. Rectangular
            black/white buttons, no count badges: the reference site uses just
            plain labels ("ALL", "COMMUNITY", "APARTMENT"). On mobile the row
            centers and wraps to match the clean single-column layout. */}
        {showTabs && (
          <div
            className="gallery-grid__tabs"
            role="tablist"
            aria-label="Filter photos by category"
          >
            {tabs.map((tab) => {
              // aria-selected tracks the filter in effect so the tablist always
              // reports one selected tab; the white highlight waits for an
              // explicit pick.
              const isChosen = chosenTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`gallery-grid__tab${isChosen ? ' is-active' : ''}`}
                  onClick={() => setChosenTab(tab)}
                >
                  {tab}
                </button>
              );
            })}
          </div>
        )}

        {/* Uniform multi-column grid on desktop/tablet; single stacked column
            inset from the screen edges on mobile (no lightbox). Every tile
            shares the same aspect ratio so the grid reads as a clean, even
            matrix. */}
        <div className="gallery-grid__grid">
          {visibleImages.map((img, index) => (
            <button
              key={index}
              type="button"
              className="gallery-grid__item gallery-item"
              onClick={() => openLightbox(index)}
            >
              <VibesitesImage
                url={img.image || img.original_url || ''}
                alt={img.alt_text || img.title || ''}
                className="gallery-grid__img"
                sizes="(min-width: 1080px) 260px, (min-width: 640px) 33vw, 100vw"
              />
            </button>
          ))}
        </div>
      </div>

      <Lightbox
        images={visibleImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GalleryStyles />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Desktop lightbox. Structurally identical to the parent theme's
// `_galleryShared.tsx`, ported here so we don't reach into an unexported
// sibling module of the parent package. Mobile uses a stacked column instead.
// ─────────────────────────────────────────────────────────────────────────────

interface LightboxProps {
  images: GalleryImage[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}

function Lightbox({ images, selectedIndex, onClose, onChange }: LightboxProps) {
  const total = images.length;

  const goNext = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex + 1) % total);
    },
    [onChange, selectedIndex, total],
  );

  const goPrev = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(
        selectedIndex === null ? null : (selectedIndex - 1 + total) % total,
      );
    },
    [onChange, selectedIndex, total],
  );

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, goNext, goPrev, onClose]);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const didSwipe = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    didSwipe.current = false;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy =
        touchStartY.current === null
          ? 0
          : e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;
      if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy)) return;
      didSwipe.current = true;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  const handleBackdropClick = useCallback(() => {
    if (didSwipe.current) {
      didSwipe.current = false;
      return;
    }
    onClose();
  }, [onClose]);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  if (!selectedImage) return null;

  const caption = (selectedImage.description || '').trim();

  return (
    <div
      className="gallery-lightbox"
      onClick={handleBackdropClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="gallery-lightbox__close"
        aria-label="Close lightbox"
      >
        &times;
      </button>

      {total > 1 && (
        <button
          onClick={goPrev}
          className="gallery-lightbox__arrow gallery-lightbox__arrow--prev"
          aria-label="Previous image"
        >
          <ChevronLeft aria-hidden="true" size={36} strokeWidth={2.25} />
        </button>
      )}

      <div
        className="gallery-lightbox__img-wrap"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <VibesitesImage
          url={selectedImage.image || selectedImage.original_url || ''}
          alt={selectedImage.alt_text || selectedImage.title || ''}
          className="max-w-full max-h-[90vh] object-contain gallery-lightbox__img"
          sizes="100vw"
        />
        {caption && (
          <div className="gallery-lightbox__caption">{caption}</div>
        )}
      </div>

      {total > 1 && (
        <button
          onClick={goNext}
          className="gallery-lightbox__arrow gallery-lightbox__arrow--next"
          aria-label="Next image"
        >
          <ChevronRight aria-hidden="true" size={36} strokeWidth={2.25} />
        </button>
      )}

      {total > 1 && (
        <div className="gallery-lightbox__counter" aria-live="polite">
          {(selectedIndex ?? 0) + 1} / {total}
        </div>
      )}
    </div>
  );
}

/**
 * All CSS for the gallery grid, category tabs, stacked mobile column, and
 * desktop lightbox. Kept in a single `<style>` block so the whole gallery
 * ships in one component with no external stylesheet.
 */
function GalleryStyles() {
  return (
    <style>{`
      .gallery-grid__container {
        max-width: 1280px;
        margin-left: auto;
        margin-right: auto;
        padding: 2.5rem 1.5rem 3.5rem;
      }

      /* ── Category tab row ─────────────────────────────────────── */
      /* Solid rectangular buttons, centered above the grid. Tabs are filled
         black with white text; both hover and the picked tab flip to a white
         background with black text inside the same black border. Each tab is
         sized by its own label, so "ALL" reads narrower than the others. */
      .gallery-grid__tabs {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.375rem;
        margin-bottom: 2.25rem;
      }

      .gallery-grid__tab {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0.85rem 1.6rem;
        font-family: var(--vibe-font-body);
        font-size: 0.8125rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #fff;
        background: #000;
        border: 1px solid #000;
        border-radius: 0;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease;
      }

      .gallery-grid__tab:hover,
      .gallery-grid__tab.is-active {
        background: #fff;
        color: #000;
      }

      .gallery-grid__tab:focus-visible {
        outline: 2px solid var(--vibe-color-primary);
        outline-offset: 2px;
      }

      /* ── Uniform image grid ───────────────────────────────────── */
      /* 4 columns on desktop, 3 on tablet, single stacked column below
         640px. Every tile shares the same aspect ratio so the grid reads
         as a clean, even matrix. */
      .gallery-grid__grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 0.75rem;
      }

      .gallery-grid__item {
        display: block;
        width: 100%;
        padding: 0;
        border: 0;
        background: none;
        overflow: hidden;
        cursor: pointer;
        position: relative;
        line-height: 0;
        aspect-ratio: 1 / 1;
      }

      .gallery-grid__img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.6s ease;
      }

      .gallery-grid__item:hover .gallery-grid__img,
      .gallery-grid__item:focus-visible .gallery-grid__img {
        transform: scale(1.05);
      }

      .gallery-grid__item:focus-visible {
        outline: 2px solid var(--vibe-color-primary);
        outline-offset: 2px;
      }

      .gallery-item {
        animation: gallery-fade-in 0.35s ease both;
      }

      @keyframes gallery-fade-in {
        from { opacity: 0; transform: scale(0.97); }
        to { opacity: 1; transform: scale(1); }
      }

      @media (prefers-reduced-motion: reduce) {
        .gallery-grid__img { transition: none; }
        .gallery-item { animation: none; }
      }

      /* ── Lightbox ─────────────────────────────────────────────── */
      .gallery-lightbox {
        position: fixed;
        inset: 0;
        z-index: 50;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.85);
        padding: 1rem;
        touch-action: pan-y;
      }

      .gallery-lightbox__img-wrap {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        max-width: calc(100vw - 8rem);
        max-height: 90vh;
        gap: 0.75rem;
      }

      .gallery-lightbox__caption {
        max-width: 42rem;
        color: rgba(255, 255, 255, 0.9);
        font-family: var(--vibe-font-body);
        font-size: 0.9rem;
        line-height: 1.45;
        text-align: center;
        letter-spacing: 0.02em;
      }

      .gallery-lightbox__close {
        position: absolute;
        top: 1rem;
        right: 1rem;
        color: #fff;
        font-size: 2.25rem;
        line-height: 1;
        background: none;
        border: none;
        cursor: pointer;
        opacity: 0.85;
        z-index: 10;
        padding: 0.25rem 0.5rem;
        transition: opacity 0.15s;
      }
      .gallery-lightbox__close:hover { opacity: 1; }

      .gallery-lightbox__arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 4.5rem;
        height: 4.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        line-height: 1;
        background: rgba(0, 0, 0, 0.35);
        border: none;
        cursor: pointer;
        padding: 0;
        border-radius: 4px;
        opacity: 0.8;
        z-index: 10;
        transition: opacity 0.15s, background 0.15s;
        -webkit-user-select: none;
        user-select: none;
      }
      .gallery-lightbox__arrow:hover {
        opacity: 1;
        background: rgba(0, 0, 0, 0.6);
      }
      .gallery-lightbox__arrow--prev { left: 0.75rem; }
      .gallery-lightbox__arrow--next { right: 0.75rem; }

      .gallery-lightbox__img {
        pointer-events: none;
        -webkit-user-drag: none;
        user-select: none;
      }

      .gallery-lightbox__counter {
        position: absolute;
        bottom: 1.25rem;
        left: 50%;
        transform: translateX(-50%);
        color: rgba(255,255,255,0.75);
        font-size: 0.875rem;
        letter-spacing: 0.05em;
        pointer-events: none;
      }

      /* ── Responsive ───────────────────────────────────────────── */
      /* Tablet: drop to 3 columns so tiles don't get uncomfortably small */
      @media (max-width: 1023px) {
        .gallery-grid__grid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
      }

      /* Mobile: full-width stacked tab toggles (black, white when selected)
         + single stacked image column inset from the screen edges so each
         photo sits on an even white gutter. No lightbox — images are static. */
      @media (max-width: 639px) {
        .gallery-grid__container {
          padding: 1.75rem 1rem 2.5rem;
        }
        .gallery-grid__tabs {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: flex-start;
          flex-wrap: nowrap;
          gap: 0.25rem;
          margin-bottom: 1.5rem;
          overflow: visible;
        }
        .gallery-grid__tab {
          flex: none;
          width: 100%;
          min-width: 0;
          max-width: none;
          padding: 0.95rem 1rem;
          font-size: 0.75rem;
          letter-spacing: 0.1em;
          text-align: center;
        }
        .gallery-grid__grid {
          grid-template-columns: 1fr;
          gap: 1rem;
        }
        .gallery-grid__item {
          aspect-ratio: 4 / 3;
          cursor: default;
          border-radius: 0;
          /* Stacked column only — no lightbox on mobile */
          pointer-events: none;
        }
        .gallery-grid__item:hover .gallery-grid__img,
        .gallery-grid__item:focus-visible .gallery-grid__img {
          transform: none;
        }
      }
    `}</style>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryGrid',
  displayName: 'Photo Gallery',
  tokens: {},
  collectionBindings: {
    'gallerygrid.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description:
        'Every photo from the property photo gallery. Photos are grouped into category tabs by their Title field ("Apartment", "Community", "Neighborhood", etc.); photos without a title fall under a "Featured" tab. When only one category is present the tab row is hidden.',
    },
  },
};

(GalleryGrid as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryGrid;
